import { Droppable } from "@hello-pangea/dnd";
import { RouteModel } from "../../domain/models/routeModel";
import {
  TruckCard,
  TruckCardContent,
  TruckCardTitle,
} from "../commons/TruckCard/TruckCard";
import { Button } from "react-bootstrap";
import { ExclamationCircle, Gear } from "react-bootstrap-icons";
import { useMemo } from "react";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../hooks/useTypedSelector";
import { updatePlanningRouteTruck } from "../../infrastructure/redux/thunks/route/updatePlanningRouteTruckThunk";
import { LoadTruck } from "../commons/Dropzone/LoadTruck";
import { RouteDraggableDealer } from "../commons/Route/RouteDraggableDealer";
import { useNavigate } from "react-router-dom";
import { TruckLicencePlateSelectField } from "../../../truck/modules/commons/TruckLicencePlateSelectField";
import LicensePlate from "license-plate";
import { useTranslation } from "react-i18next";

export const Route = ({
  route,
  portal,
}: {
  route: RouteModel;
  portal: HTMLDivElement;
}) => {
  const truck = useMemo(() => route.truck, [route.truck]);

  const capacityUsed = useMemo(() => {
    return route.packingSlips?.flatMap((dealer) =>
      dealer.requests?.filter((request) => request.type === "deliver"),
    ).length;
  }, [route.packingSlips]);

  const pickupCapacityUsed = useMemo(() => {
    return route.packingSlips?.reduce(
      (sum, slip) =>
        sum + (slip.pickup_amount != null ? Number(slip.pickup_amount) : 0),
      0,
    );
  }, [route.packingSlips]);

  const { trucks, draggableId } = useAppSelector((state) => state.planning);

  const dispatch = useAppDispatch();

  const requestDragging = useMemo(() => {
    return (
      draggableId?.startsWith("new-request:") ||
      draggableId?.startsWith("request:")
    );
  }, [draggableId]);

  const navigate = useNavigate();

  const { t } = useTranslation("common");

  return (
    <Droppable key={"route:" + route.id} droppableId={"route:" + route.id}>
      {(provided, snapshot) => (
        <div ref={provided.innerRef} {...provided.droppableProps}>
          <TruckCard className={"me-3 mb-3"}>
            <TruckCardTitle>
              <div className={"d-flex justify-content-between"}>
                {truck != null ? (
                  <div className={"d-flex flex-column"}>
                    <h3>
                      {new LicensePlate(truck.licence ?? "").pretty() ??
                        truck.title}
                    </h3>
                    <span>{route.driver?.name ?? truck?.driver?.name}</span>
                    <span
                      className={
                        capacityUsed > truck?.capacity
                          ? "badge bg-danger fw-bold"
                          : ""
                      }
                    >
                      {t("Delivery")}: {capacityUsed}/{truck?.capacity ?? "?"}{" "}
                      {capacityUsed > truck?.capacity && (
                        <ExclamationCircle size={13} />
                      )}
                    </span>
                    <span
                      className={
                        pickupCapacityUsed > truck?.capacity
                          ? "badge bg-danger fw-bold"
                          : ""
                      }
                    >
                      {t("Pickup")}: {pickupCapacityUsed}/
                      {truck?.capacity ?? "?"}{" "}
                      {pickupCapacityUsed > truck?.capacity && (
                        <ExclamationCircle size={13} />
                      )}
                    </span>
                  </div>
                ) : (
                  <div className="w-100 me-2">
                    <TruckLicencePlateSelectField
                      value={null}
                      isMulti={false}
                      onChange={(selectedTruck: unknown) => {
                        if (
                          !selectedTruck ||
                          (Array.isArray(selectedTruck) &&
                            selectedTruck.length === 0)
                        )
                          return;
                        const selected = Array.isArray(selectedTruck)
                          ? selectedTruck[0]
                          : selectedTruck;
                        const truck = trucks.find(
                          (t) =>
                            t.id ===
                            (selected as { value: string | number }).value,
                        );
                        if (truck) {
                          void dispatch(
                            updatePlanningRouteTruck({
                              route,
                              truck,
                            }),
                          );
                        }
                      }}
                      isInvalid={false}
                    />
                  </div>
                )}

                <div>
                  <Button
                    size={"sm"}
                    className={"truck-btn"}
                    onClick={() => {
                      navigate(`/planning/daily-planning/${route.id}`);
                    }}
                  >
                    <Gear size={20} />
                  </Button>
                </div>
              </div>
            </TruckCardTitle>
            <TruckCardContent>
              <div
                className={"text-center"}
                style={{
                  display:
                    requestDragging && snapshot.isDraggingOver
                      ? "block"
                      : "none",
                }}
              >
                <LoadTruck />
              </div>
              <div
                style={{
                  display:
                    requestDragging && snapshot.isDraggingOver
                      ? "none"
                      : "block",
                }}
              >
                {route.packingSlips?.map((packingSlip, index) => (
                  <RouteDraggableDealer
                    key={"dealer:" + packingSlip.id}
                    packingSlip={packingSlip}
                    index={index}
                    draggableId={draggableId}
                    portal={portal}
                  />
                ))}
                {provided.placeholder}
              </div>
            </TruckCardContent>
          </TruckCard>
        </div>
      )}
    </Droppable>
  );
};
